import * as d3 from 'd3'
import * as dc from 'dc'
import { BUY_COLOR, SELL_COLOR } from '@/utils/flowSightColors'
import { formatCompactAmount } from '@/utils/utils'
import { getChartTooltip, escapeHtml } from '../runtime/chartTooltip.js'
import {
  MATURITY_AXIS_MAX,
  totalVolumeOf,
  averageMaturityOf,
  dailySeries,
  collectBuckets,
  hasRows,
} from '../calculations/clientPortfolio.js'

// One SVG for the WHOLE panel, not two dc charts per row. With 58 clients that
// would be 116 dc instances, each with its own group and render cycle, and
// nothing would guarantee the rows line up vertically.
//
// It still behaves like a dc chart from the outside: the object returned at the
// bottom is registered in the chart group, so safeRenderAll/safeRedrawAll and
// the sidebar filters drive it exactly like every other chart on the tab. Same
// trick createVolumeBySideChart already uses.

// A max bubble is 2 * MAX_BUBBLE_R tall, so the row has to clear 66px plus
// breathing room - these two move together.
const MAX_BUBBLE_R = 33
const ROW_HEIGHT = 76
const SPARK_W = 92
// 20 chars at 11px measure ~112px, so the column only needs to clear that.
const NAME_MAX_CHARS = 20
const NAME_W = 122
const VALUE_W = 78
const GUTTER = 10
const AXIS_H = 22
// The radius domain stops at the 95th percentile instead of the maximum, and
// anything above it is clamped to the largest circle. The distribution is very
// long-tailed - one bubble is R$ 1.67Bi against a p95 of R$ 430Mi - and scaling
// to the max turned the whole body of the data into dots: the p90 bubble came
// out at 13px. The cost is that the top 5% no longer differ in size from each
// other; the tooltip still gives their exact value.
const RADIUS_DOMAIN_QUANTILE = 0.95

// The maturity axis is compressed: short tenors carry most of the flow, so half
// the lane is spent on 0-90d and the remaining 360 days share the other half.
//
// symlog, not log: a log scale has no room for 0, and tenorDays can be 0. The
// constant is solved rather than guessed - for a target T at the midpoint of an
// axis ending at M, log(1+T/C)/log(1+M/C) = 1/2 gives (1+u)² = 1+(M/T)u with
// u = T/C, so u = M/T - 2 and C = T / (M/T - 2). With T=90 and M=450 that is
// exactly 30.
const AXIS_HALFWAY_DAYS = 90
const SYMLOG_CONSTANT = AXIS_HALFWAY_DAYS / (MATURITY_AXIS_MAX / AXIS_HALFWAY_DAYS - 2)
// Chosen to spread evenly once transformed; evenly spaced day values would
// bunch against the right edge.
const AXIS_TICK_DAYS = [0, 15, 30, 60, 90, 180, 300, MATURITY_AXIS_MAX]

export function createClientPortfolioPanel ({ el, chartGroup, dim, group, t, uid, sideColor, rowsProvider, amountOf, grain }) {
  const host = el
  if (!host) return null

  const tooltip = getChartTooltip()
  let svg = null

  // Dois SVGs irmaos dentro do host: o do eixo e curto e gruda no topo da area
  // rolavel; o do corpo e alto e rola. A alternativa - o componente Vue
  // desenhar seu proprio eixo fixo - duplicaria a aritmetica de `laneLeft` e
  // `laneWidth` fora daqui, e ela divergiria no primeiro resize. Emitindo os
  // dois do mesmo lugar, o alinhamento e garantido por construcao.
  const axisHost = document.createElement('div')
  axisHost.className = 'portfolio-axis'
  const bodyHost = document.createElement('div')
  bodyHost.className = 'portfolio-body'
  host.appendChild(axisHost)
  host.appendChild(bodyHost)

  const laneLeft = () => SPARK_W + GUTTER + NAME_W + GUTTER
  const laneWidth = () => Math.max(220, (host.clientWidth || 900) - laneLeft() - VALUE_W - GUTTER)

  // Union of every day present, so all sparklines share one x scale: a peak in
  // row 3 sits above the same date as a peak in row 1.
  // Lida a cada desenho, nao capturada na criacao: a janela do RFQ e um filtro
  // do crossfilter e nao reconstroi o painel, entao a granularidade precisa ser
  // reavaliada no redraw. Com janela de 1 dia o eixo por dia teria um ponto so.
  const currentGrain = () => (typeof grain === 'function' ? grain() : grain) || 'DAY'

  // client -> raw rows, for transaction mode.
  const transactionsByClient = () => {
    const out = new Map()
    const rows = (typeof rowsProvider === 'function' ? rowsProvider() : []) || []
    rows.forEach((r) => {
      const name = String(r?.counterparty || '').trim()
      if (!name) return
      if (!out.has(name)) out.set(name, [])
      out.get(name).push(r)
    })
    return out
  }

  const rowsFromGroup = () => group.all()
    .filter((d) => hasRows(d.value))
    .sort((a, b) => totalVolumeOf(b.value) - totalVolumeOf(a.value))

  const infoRow = (label, v) =>
    `<div><span style="color:var(--muted,#9aa3af)">${escapeHtml(label)}:</span> ${escapeHtml(v)}</div>`

  // Hovering the CLIENT gives the client's totals...
  const clientTooltip = (name, value) => {
    const avg = averageMaturityOf(value)
    return [
      `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(name)}</div>`,
      infoRow(t('flowSightPortfolio.negotiatedVolume'), `R$ ${formatCompactAmount(totalVolumeOf(value))}`),
      infoRow(t('flowSightPortfolio.operations'), value.cnt),
      infoRow(t('flowSightPortfolio.avgMaturity'), avg === null ? '—' : `${avg.toFixed(0)}d`),
    ].join('')
  }

  const txTooltip = (name, row, amount) => {
    const side = String(row?.side || '').toUpperCase() === 'BUY' ? 'BUY' : 'SELL'
    const color = side === 'BUY' ? BUY_COLOR : SELL_COLOR
    const when = row?.rfqtimestamp instanceof Date ? row.rfqtimestamp.toLocaleDateString() : ''
    return [
      `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(name)}</div>`,
      `<div style="margin-bottom:2px"><span style="color:${color};font-weight:700">${side}</span> • ${escapeHtml(`${Number(row?.tenorDays) || 0}d`)}</div>`,
      infoRow(t('flowSightPortfolio.negotiatedVolume'), `R$ ${formatCompactAmount(amount)}`),
      when ? infoRow(t('flowSightPortfolio.rfqDate'), when) : '',
    ].join('')
  }

  const render = () => {
    const rows = rowsFromGroup()
    const grainNow = currentGrain()
    const days = collectBuckets(rows, grainNow)
    const width = Math.max(640, host.clientWidth || 900)
    // So um eixo agora, no topo e fixo. O de baixo existia porque a janela de
    // 70vh sempre deixava uma borda longe do eixo do topo; com a pagina inteira
    // rolando e o eixo grudado, ele virou redundante.
    const height = rows.length * ROW_HEIGHT

    d3.select(axisHost).selectAll('svg').remove()
    d3.select(bodyHost).selectAll('svg').remove()

    const axisSvg = d3.select(axisHost)
      .append('svg')
      .attr('class', 'portfolio-panel portfolio-panel--axis')
      .attr('width', width)
      .attr('height', AXIS_H)

    svg = d3.select(bodyHost)
      .append('svg')
      .attr('class', 'portfolio-panel')
      .attr('width', width)
      .attr('height', height)

    if (!rows.length) return

    const lane = d3.scaleSymlog()
      .constant(SYMLOG_CONSTANT)
      .domain([0, MATURITY_AXIS_MAX])
      .range([0, laneWidth()])
    // Radius by AREA over a domain anchored at 0, so a bubble twice the size
    // means twice the volume - see the note in createConversionBubbleChart.
    const byClient = transactionsByClient()
    const amountOfRow = (r) => Math.abs(Number(typeof amountOf === 'function' ? amountOf(r) : 0) || 0)

    const bubbleValues = Array.from(byClient.values()).flat()
      .map(amountOfRow)
      .filter((v) => v > 0)
      .sort(d3.ascending)
    const radiusTop = d3.quantile(bubbleValues, RADIUS_DOMAIN_QUANTILE) || d3.max(bubbleValues) || 1
    const radius = d3.scaleSqrt().domain([0, radiusTop]).range([0, MAX_BUBBLE_R]).clamp(true)
    const sparkX = d3.scalePoint().domain(days).range([0, SPARK_W]).padding(0.5)

    const axis = (y) => {
      const g = axisSvg.append('g').attr('transform', `translate(${laneLeft()},${y})`)
      AXIS_TICK_DAYS.forEach((d) => {
        g.append('text')
          .attr('x', lane(d))
          .attr('y', 0)
          .attr('text-anchor', 'middle')
          .attr('style', 'font-size:10px; fill:var(--muted, #9aa3af)')
          .text(d === MATURITY_AXIS_MAX ? `${d}d+` : `${d}d`)
      })
    }
    axis(AXIS_H - 8)

    const trendColor = (typeof sideColor === 'function' ? sideColor() : sideColor) || SELL_COLOR
    // Sem deslocamento vertical: o eixo saiu deste SVG.
    const body = svg.append('g')

    // Shared by both modes, so the row furniture can't drift between them.
    const drawAverageTick = (laneG, value) => {
      const avg = averageMaturityOf(value)
      if (avg === null) return
      laneG.append('rect')
        .attr('x', lane(Math.min(avg, MATURITY_AXIS_MAX)) - 1.5)
        .attr('y', ROW_HEIGHT / 2 - ROW_HEIGHT * 0.3)
        .attr('width', 3)
        .attr('height', ROW_HEIGHT * 0.6)
        .attr('fill', '#f2f2f2')
    }

    const drawTotal = (row, value) => {
      row.append('text')
        .attr('x', width - GUTTER)
        .attr('y', ROW_HEIGHT / 2 + 4)
        .attr('text-anchor', 'end')
        .attr('style', 'font-size:10px; fill:var(--muted, #9aa3af)')
        .text(`R$ ${formatCompactAmount(totalVolumeOf(value))}`)
    }

    // Covers the sparkline + name columns ONLY. A full-row overlay was painted
    // after the bubbles, so it sat on top of them and swallowed their hover.
    const drawClientHover = (row, key, value) => {
      row.append('rect')
        .attr('x', 0).attr('y', 0)
        .attr('width', SPARK_W + GUTTER + NAME_W).attr('height', ROW_HEIGHT)
        .attr('fill', 'transparent')
        .style('cursor', 'default')
        .on('mouseover', () => tooltip.show(clientTooltip(key, value), d3.event))
        .on('mousemove', () => tooltip.move(d3.event))
        .on('mouseout', () => tooltip.hide())
    }

    rows.forEach(({ key, value }, i) => {
      const y = i * ROW_HEIGHT
      const row = body.append('g').attr('transform', `translate(0,${y})`)

      row.append('rect')
        .attr('x', 0).attr('y', 0)
        .attr('width', width).attr('height', ROW_HEIGHT)
        .attr('fill', i % 2 ? 'rgba(255,255,255,.02)' : 'transparent')

      // --- sparkline: negotiated volume per day, scaled to THIS client's own
      // peak. A shared scale flattened everyone below the top few into a
      // straight line; the row is here to show a shape over time, and absolute
      // size is already carried by the total and the bubbles.
      const series = dailySeries(value, days, grainNow)
      const spark = d3.scaleLinear()
        .domain([0, d3.max(series, (p) => p.value) || 1])
        .range([ROW_HEIGHT - 10, 4])
      if (series.length > 1) {
        const area = d3.area()
          .x((p) => sparkX(p.day))
          .y0(ROW_HEIGHT - 10)
          .y1((p) => spark(p.value))
          .curve(d3.curveMonotoneX)
        row.append('path')
          .attr('d', area(series))
          .attr('fill', trendColor)
          .attr('fill-opacity', 0.35)
        row.append('path')
          .attr('d', d3.line().x((p) => sparkX(p.day)).y((p) => spark(p.value)).curve(d3.curveMonotoneX)(series))
          .attr('fill', 'none')
          .attr('stroke', trendColor)
          .attr('stroke-width', 1.2)
      }

      // --- client name
      row.append('text')
        .attr('x', SPARK_W + GUTTER)
        .attr('y', ROW_HEIGHT / 2 + 4)
        .attr('style', 'font-size:11px; fill:#fff !important')
        // Display only - the tooltip always carries the full name.
        .text(key.length > NAME_MAX_CHARS ? `${key.slice(0, NAME_MAX_CHARS - 1)}…` : key)

      // --- maturity lane
      const laneG = row.append('g').attr('transform', `translate(${laneLeft()},0)`)
      laneG.append('line')
        .attr('x1', 0).attr('x2', laneWidth())
        .attr('y1', ROW_HEIGHT / 2).attr('y2', ROW_HEIGHT / 2)
        .attr('stroke', 'var(--border, #2a2e36)')
        .attr('stroke-width', 1)

      // One circle per transaction, largest first so the small ones stay on
      // top. No labels: at this density there is no room for text, and many
      // transactions share an exact maturity so they land on the same x.
      {
        const txs = (byClient.get(key) || []).slice().sort((a, c) => amountOfRow(c) - amountOfRow(a))
        txs.forEach((r) => {
          const amount = amountOfRow(r)
          if (amount <= 0) return
          const side = String(r?.side || '').toUpperCase() === 'BUY' ? 'BUY' : 'SELL'
          const color = side === 'BUY' ? BUY_COLOR : SELL_COLOR
          laneG.append('circle')
            .attr('cx', lane(Math.min(Number(r?.tenorDays) || 0, MATURITY_AXIS_MAX)))
            .attr('cy', ROW_HEIGHT / 2)
            .attr('r', Math.max(1.5, radius(amount)))
            .attr('fill', color)
            .attr('fill-opacity', 0.35)
            .attr('stroke', color)
            .attr('stroke-width', 0.6)
            .attr('stroke-opacity', 0.8)
            .on('mouseover', () => tooltip.show(txTooltip(key, r, amount), d3.event))
            .on('mousemove', () => tooltip.move(d3.event))
            .on('mouseout', () => tooltip.hide())
        })
      }

      drawAverageTick(laneG, value)
      drawTotal(row, value)
      drawClientHover(row, key, value)
    })
  }

  // Looks like a dc chart to the registry: safeRenderAll/safeRedrawAll and the
  // sidebar filters drive it with no special casing.
  const chartObj = {
    render: () => render(),
    redraw: () => render(),
    filterAll: () => chartObj,
    filters: () => [],
    anchorName: () => `flowsightPortfolio_${uid}`,
    dimension: () => dim,
  }

  dc.registerChart(chartObj, chartGroup)
  render()
  return chartObj
}
